import { useState } from 'react';
import {
  ActionIcon,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { PiMagnifyingGlass, PiPencil, PiPlus, PiTrash } from 'react-icons/pi';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { useUrlDraft, useUrlState } from '@/hooks/useUrlState';
import { urlField } from '@/lib/urlState';
import {
  REFERRING_AGENTS_ALL,
  REFERRING_AGENTS_WRITE,
  useHasAnyPermission,
} from '@/models/permissions';
import {
  type ReferringAgent,
  useDeleteReferringAgent,
  useReferringAgents,
} from '@/models/referringAgents';
import ReferringAgentModal from '@/pages/referring-agents/ReferringAgentModal';

const AGENTS_SCHEMA = { term: urlField.string() };

export default function ReferringAgentsPage() {
  const [state, setState, { clear, isFiltered }] = useUrlState(AGENTS_SCHEMA);
  const [term, setTerm] = useUrlDraft(state.term, (next) =>
    setState({ term: next }),
  );
  const { data, isLoading } = useReferringAgents(state.term.trim());
  const agents = data?.agents ?? [];

  const canWrite = useHasAnyPermission([
    REFERRING_AGENTS_WRITE,
    REFERRING_AGENTS_ALL,
  ]);
  const [editing, setEditing] = useState<ReferringAgent | undefined>();
  const [formOpen, { open: openForm, close: closeForm }] = useDisclosure(false);
  const remove = useDeleteReferringAgent();

  const startCreate = () => {
    setEditing(undefined);
    openForm();
  };

  const startEdit = (agent: ReferringAgent) => {
    setEditing(agent);
    openForm();
  };

  const confirmDelete = (agent: ReferringAgent) =>
    modals.openConfirmModal({
      title: `Delete ${agent.name}?`,
      children: (
        <Text size="sm">
          Leads and contacts that already reference this agent keep it - nothing
          is unassigned or lost. Only the referring agent picker stops offering
          it going forward.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () =>
        remove.mutate(agent.id, {
          onError: (err) =>
            notifications.show({ color: 'red', message: String(err) }),
        }),
    });

  return (
    <Paper p="md" h="100%">
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap">
          <Group gap="xs">
            <Title order={3}>Referring Agents</Title>
            <Text size="sm" c="dimmed">
              {data?.total ?? 0} total
            </Text>
          </Group>
          <Group gap="xs" wrap="wrap">
            <TextInput
              size="xs"
              placeholder="Search by name..."
              leftSection={<PiMagnifyingGlass size={14} />}
              value={term}
              onChange={(e) => setTerm(e.currentTarget.value)}
              w={220}
            />
            <ClearFiltersButton visible={isFiltered} onClick={clear} />
            {canWrite && (
              <Button
                size="xs"
                leftSection={<PiPlus size={14} />}
                onClick={startCreate}
              >
                New agent
              </Button>
            )}
          </Group>
        </Group>

        {isLoading ? (
          <Loader />
        ) : agents.length === 0 ? (
          <Paper withBorder p="md">
            <Text c="dimmed" size="sm">
              No referring agents found.
            </Text>
          </Paper>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th w={120}>Leads</Table.Th>
                <Table.Th w={120}>Contacts</Table.Th>
                <Table.Th w={100} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {agents.map((a) => (
                <Table.Tr key={a.id}>
                  <Table.Td fw={500}>{a.name}</Table.Td>
                  <Table.Td>{a.leadCount ?? 0}</Table.Td>
                  <Table.Td>{a.contactCount ?? 0}</Table.Td>
                  <Table.Td>
                    {canWrite && (
                      <Group gap="xs" wrap="nowrap" justify="flex-end">
                        <Tooltip label="Rename">
                          <ActionIcon
                            variant="subtle"
                            onClick={() => startEdit(a)}
                          >
                            <PiPencil size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => confirmDelete(a)}
                          >
                            <PiTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        <ReferringAgentModal
          opened={formOpen}
          onClose={closeForm}
          agent={editing}
        />
      </Stack>
    </Paper>
  );
}
